import type { ReactNode } from 'react';
import { View } from 'react-native';

import { Box } from '../primitives/Box';
import { Card } from '../primitives/Card';
import { Divider } from '../primitives/Divider';
import { Switch } from '../primitives/Switch';
import { Text } from '../primitives/Text';
import { useTheme } from '../theme/ThemeProvider';

export interface NotificationSettingsLabels {
  title: string;
  description: string;
  enableLabel: string;
  unsupported: string;
  criticalLabel: string;
  criticalDescription: string;
}

export interface NotificationSettingsProps {
  supported: boolean;
  enabled: boolean;
  onToggleEnabled: (next: boolean) => void;
  busy?: boolean;
  criticalSupported?: boolean;
  criticalEnabled?: boolean;
  onToggleCritical?: (next: boolean) => void;
  labels: NotificationSettingsLabels;
  footer?: ReactNode;
}

export function NotificationSettings({
  supported,
  enabled,
  onToggleEnabled,
  busy = false,
  criticalSupported = false,
  criticalEnabled = false,
  onToggleCritical,
  labels,
  footer,
}: NotificationSettingsProps) {
  const theme = useTheme();
  const showCritical = criticalSupported && enabled;

  return (
    <Card>
      <Box gap={theme.spacing(1)}>
        <Text variant="title">{labels.title}</Text>
        <Text variant="muted">{labels.description}</Text>
      </Box>

      <View style={{ height: theme.spacing(3) }} />

      <Box row center style={{ justifyContent: 'space-between' }} gap={theme.spacing(3)}>
        <Box flex={1}>
          <Text weight="medium">{labels.enableLabel}</Text>
          {!supported ? <Text variant="caption">{labels.unsupported}</Text> : null}
        </Box>
        <Switch
          value={enabled}
          disabled={!supported || busy}
          onValueChange={onToggleEnabled}
          accessibilityLabel={labels.enableLabel}
        />
      </Box>

      {showCritical ? (
        <>
          <Divider spacing={theme.spacing(3)} />
          <Box row center style={{ justifyContent: 'space-between' }} gap={theme.spacing(3)}>
            <Box flex={1} gap={theme.spacing(0.5)}>
              <Text weight="medium">{labels.criticalLabel}</Text>
              <Text variant="caption">{labels.criticalDescription}</Text>
            </Box>
            <Switch
              value={criticalEnabled}
              disabled={busy}
              onValueChange={onToggleCritical}
              accessibilityLabel={labels.criticalLabel}
            />
          </Box>
        </>
      ) : null}

      {footer ? (
        <>
          <Divider spacing={theme.spacing(3)} />
          {footer}
        </>
      ) : null}
    </Card>
  );
}
